import React, { useState, useEffect, useReducer, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import { useMenuStyles } from "../../components/menuStyles";
import Checkbox from "@material-ui/core/Checkbox";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import clsx from "clsx";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import FolderIcon from "@material-ui/icons/FolderOutlined";
import ProjectIcon from "@material-ui/icons/LayersOutlined";
import CopyIcon from "@material-ui/icons/FileCopyOutlined";
import AllIcon from "@material-ui/icons/AppsOutlined";
import FlowIcon from "@material-ui/icons/AccountTreeOutlined";
import InboxIcon from "@material-ui/icons/MoveToInboxOutlined";
import PauseIcon from "@material-ui/icons/PauseCircleOutline";
import PlayIcon from "@material-ui/icons/PlayCircleOutline";
import { format } from "date-fns";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { connectToSocket } from "../../services/socket";
import { i18n } from "../../translate/i18n";
import { useProject } from "../../context/Project/ProjectContext";
import { useConfirm } from "../../components/ConfirmDialog";
import { useSortable } from "../../utils/useSortable";

const appVal = (a, key) => {
  switch (key) {
    case "name": return a.name || "";
    case "trigger": return a.triggerType || a.trigger || "";
    case "status": return a.publishedVersion ? (a.dirty ? 1 : 2) : 0;
    case "updatedAt": return a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    case "lastRunAt": return a.lastRunAt ? new Date(a.lastRunAt).getTime() : 0;
    default: return "";
  }
};

// design tokens (refactor) — fonte única; promover para o tema ao migrar as demais telas
const T = {
  bg: "#F6F7F9",
  panel: "#FFFFFF",
  line: "#E6E9ED",
  lineSoft: "#EEF0F3",
  hover: "#FAFBFC",
  ink: "#1B222A",
  ink2: "#5B6470",
  ink3: "#929AA4",
  char: "#2C353D",
  green: "#CEFF00",
  ok: "#1E9E62",
  warn: "#C77A12",
  danger: "#C0453B",
  info: "#2F6FB0",
  alt: "#6A52C4",
  mono: 'ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace',
};

// mesma tipografia do menu lateral (Inter, leve e limpa)
const MENU_FONT = 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// Projeto protegido (hardcoded): apps aqui não podem ser apagados nem editados —
// só é possível mexer depois de MOVER para outro projeto. Mantém os fluxos de
// sistema seguros. Ver mesma checagem no Builder (modo read-only forçado).
const PROTECTED_PROJECT = "Sistema Numera";
const PROTECTED_MSG = "Apps no projeto Sistema Numera não podem ser alterados";

const useStyles = makeStyles(theme => ({
  page: { display: "flex", height: "100%", minHeight: 0, background: T.bg },
  // ---- folders rail ----
  side: {
    width: 228,
    flexShrink: 0,
    borderRight: `1px solid ${T.line}`,
    background: T.panel,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("xs")]: { width: 188 },
  },
  sideHead: { display: "flex", alignItems: "center", padding: theme.spacing(2, 2, 1) },
  sideTitle: { fontFamily: MENU_FONT, fontWeight: 500, fontSize: 13, color: T.ink2 },
  sideCount: { marginLeft: "auto", fontFamily: T.mono, fontSize: 11.5, color: T.ink3 },
  folderAddBtn: { marginLeft: 6, padding: 3, color: T.ink3, "&:hover": { color: T.ink, background: T.hover } },
  list: { flex: 1, overflowY: "auto", padding: theme.spacing(0.5, 1) },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 10px",
    margin: "1px 0",
    borderRadius: 7,
    cursor: "pointer",
    color: T.ink2,
    position: "relative",
    "&:hover": { background: T.lineSoft },
    "&:hover $itemMenu": { visibility: "visible" },
  },
  itemActive: {
    background: "#F0F2F5",
    color: T.ink,
    "&::before": {
      content: '""',
      position: "absolute",
      left: -4,
      top: 6,
      bottom: 6,
      width: 2.5,
      borderRadius: 2,
      background: theme.app.green,
    },
  },
  itemIcon: { display: "flex", fontSize: 17, opacity: 0.75 },
  itemLabel: { flex: 1, fontFamily: MENU_FONT, fontSize: 14, fontWeight: 400, letterSpacing: "-0.006em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  itemCount: { fontFamily: T.mono, fontSize: 11.5, color: T.ink3 },
  itemMenu: { visibility: "hidden", padding: 2, color: "inherit" },
  sep: { margin: theme.spacing(0.75, 1.25), borderTop: `1px solid ${T.lineSoft}` },
  newProjRow: { display: "flex", gap: 6, padding: theme.spacing(0.5) },
  // ---- main / content (largura contida) ----
  main: { flex: 1, minWidth: 0, overflowY: "auto" },
  wrap: { width: "100%", maxWidth: 1200, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { display: "flex", alignItems: "center", gap: 10, marginBottom: theme.spacing(2.5) },
  headIcon: { width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#EEF0F3", color: "#5B6470" },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: T.ink },
  titleCount: { fontFamily: T.mono, fontSize: 12, color: T.ink3 },
  grow: { flexGrow: 1 },
  search: { width: 240 },
  primaryBtn: {
    background: T.char,
    color: "#fff",
    fontSize: 12.5,
    fontWeight: 500,
    textTransform: "none",
    padding: "7px 14px",
    borderRadius: 8,
    boxShadow: "none",
    "&:hover": { background: "#1f262c", boxShadow: "none" },
    "&.Mui-disabled": { background: T.line, color: T.ink3 },
  },
  // ---- bulk bar ----
  bulkBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    marginBottom: theme.spacing(1.5),
    borderRadius: 8,
    background: T.lineSoft,
    border: `1px solid ${T.line}`,
  },
  bulkBarHidden: { visibility: "hidden", pointerEvents: "none" },
  selCtl: { display: "flex", alignItems: "center", gap: 0 },
  selCaret: { padding: 2, marginLeft: -6, color: T.ink3 },
  bulkHint: { color: T.ink3, fontSize: 12.5 },
  bulkCount: { fontWeight: 600, fontSize: 13, marginRight: theme.spacing(1), color: T.ink },
  // ---- table ----
  tbl: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 10, overflow: "hidden" },
  headCell: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: T.ink3,
    fontWeight: 600,
    borderBottom: `1px solid ${T.line}`,
    padding: "0 16px",
    height: 38,
    lineHeight: "38px",
  },
  row: {
    cursor: "pointer",
    "&:hover": { backgroundColor: T.hover },
    "&:hover $kebab": { visibility: "visible" },
  },
  cell: { borderBottom: `1px solid ${T.lineSoft}`, padding: "0 16px", height: 46, fontSize: 13, color: T.ink },
  name: { fontWeight: 500, color: T.ink },
  mono: { fontFamily: T.mono, fontSize: 11.5, color: T.ink2 },
  badge: { fontFamily: T.mono, fontSize: 11, padding: "2px 8px", borderRadius: 5, fontWeight: 500, display: "inline-block", textTransform: "lowercase" },
  bInbound: { color: T.info, background: "#EAF1F8" },
  bOutbound: { color: T.alt, background: "#F0EDFA" },
  status: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: T.ink2 },
  dot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  ver: { fontFamily: T.mono, fontSize: 11, color: T.ink3, marginLeft: 1 },
  kebab: { opacity: 0.55, color: T.ink3, transition: "opacity .12s", "&:hover": { color: T.ink } },
  empty: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 16, padding: theme.spacing(7, 3), textAlign: "center", color: T.ink3, fontSize: 13.5 },

  // ---- lista de cards (apps) ----
  appList: { display: "flex", flexDirection: "column", gap: 10 },
  appCard: {
    display: "flex", alignItems: "center", gap: 14, background: T.panel,
    border: `1px solid ${T.line}`, borderRadius: 16, padding: "12px 16px", cursor: "pointer",
    transition: "box-shadow .15s ease, border-color .15s ease",
    "&:hover": { boxShadow: "0 4px 16px rgba(27,34,42,.06)" },
    "&:hover $kebab": { opacity: 1 },
  },
  appCardSel: { background: "#F4FBE0", boxShadow: `inset 2.5px 0 0 0 ${theme.app.green}` },
  // fluxo desativado: card apagado + faixa cinza à esquerda + avatar neutro
  appCardInactive: {
    background: T.hover || "#FAFBFC",
    boxShadow: "inset 3px 0 0 0 #9AA3AD",
    "& $appAvatar": { background: "#EEF0F3", color: "#9AA3AD" },
    "& $appName": { color: T.ink2 },
  },
  appAvatar: {
    width: 40, height: 40, borderRadius: 999, flexShrink: 0, background: "#EFF8D6", color: "#5f7a12",
    display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 20 },
  },
  appMain: { flex: 1, minWidth: 0 },
  nameRow: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  appName: { fontWeight: 600, fontSize: 14, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  appNote: {
    fontSize: 12.5, color: T.textSecondary, marginTop: 2,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 420,
  },
  // selo forte de "Desativado" ao lado do nome
  deactivatedPill: {
    display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0,
    fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
    color: "#5B6470", background: "#E7EAEE", border: "1px solid #D3D8DE",
    borderRadius: 999, padding: "1.5px 7px 1.5px 5px", lineHeight: 1.4,
    "& svg": { fontSize: 11 },
  },
  colStatus: { width: 150, flexShrink: 0, minWidth: 0 },
  colDate: { width: 140, flexShrink: 0 },
  colDateVal: { fontFamily: T.mono, fontSize: 12, color: T.ink3 },
  infoLabel: { fontSize: 10, textTransform: "uppercase", letterSpacing: ".05em", color: T.ink3, fontWeight: 600, marginBottom: 4 },

  // ---- skeleton de carregamento ----
  "@keyframes skShimmer": { "0%": { backgroundPosition: "-320px 0" }, "100%": { backgroundPosition: "320px 0" } },
  skCard: { cursor: "default", pointerEvents: "none", "&:hover": { boxShadow: "none" } },
  skBox: {
    background: `linear-gradient(90deg, ${T.line || "#eceef1"} 25%, ${T.hover || "#f4f6f8"} 50%, ${T.line || "#eceef1"} 75%)`,
    backgroundSize: "640px 100%",
    animation: "$skShimmer 1.3s ease-in-out infinite",
    borderRadius: 6,
  },
  skAvatar: { width: 40, height: 40, borderRadius: 999, flexShrink: 0 },
  skLabel: { height: 8, width: 42, borderRadius: 4, marginBottom: 8 },
  skLine: { height: 13, borderRadius: 6 },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":
      return action.payload;
    case "UPSERT": {
      const idx = state.findIndex(a => a.id === action.payload.id);
      if (idx >= 0) {
        const next = [...state];
        next[idx] = { ...next[idx], ...action.payload };
        return next;
      }
      return [action.payload, ...state];
    }
    case "DELETE":
      return state.filter(a => a.id !== Number(action.payload));
    default:
      return state;
  }
};

// remembers the last selected folder per project (so returning to the page
// reopens it). "all" | "none" | folderId.
const folderStoreKey = pid => `numera:appsFolder:${pid}`;

// 1ª linha com conteúdo da nota, sem marcação markdown — vira o subtítulo do
// card. Títulos ("## O que faz") são pulados: o valor está no texto que vem
// depois deles, não no rótulo.
const resumoNota = md => {
  if (!md) return "";
  const linha = String(md)
    .split("\n")
    .map(l => l.replace(/^\s*[#>*-]+\s*/, "").replace(/\*\*/g, "").trim())
    .find(l => l.length > 2);
  return linha ? linha.slice(0, 160) : "";
};

const Applications = () => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const history = useHistory();
  const { activeProjectId, projects } = useProject();
  // projeto ativo protegido? (a lista só mostra apps do projeto ativo, então todos
  // ficam read-only quando o projeto é o "Sistema Numera")
  const activeProject = projects.find(p => p.id === activeProjectId);
  const isProtected = !!activeProject && activeProject.name === PROTECTED_PROJECT;
  const { confirm, prompt } = useConfirm();
  const [apps, dispatch] = useReducer(reducer, []);
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState("all"); // "all" | "none" | folderId(number)
  const [searchParam, setSearchParam] = useState("");
  const [newFolder, setNewFolder] = useState(null); // string while creating, null otherwise
  const [selectedIds, setSelectedIds] = useState([]); // checkbox multi-selection
  const [appMenu, setAppMenu] = useState(null); // { anchor, app }
  const [pickMenu, setPickMenu] = useState(null); // { anchor, onPick }
  const [projMenu, setProjMenu] = useState(null); // { anchor, app } — seletor de projeto
  const [selMenu, setSelMenu] = useState(null); // { anchor } — menu do cabeçalho (todos/nenhum/ativos/inativos)
  const [loading, setLoading] = useState(true); // skeleton no 1º carregamento

  const fetchApps = useCallback(async () => {
    if (!activeProjectId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/applications", { params: { projectId: activeProjectId } });
      // tools/agents vivem em "Agentes de IA" e collectors em "Coletores" — nenhum
      // deles entra na lista de automações (data_collector, sim: é a consumidora)
      const onlyApps = (data.applications || []).filter(
        a => a.triggerType !== "tool" && a.triggerType !== "agent" && a.triggerType !== "collector" && a.triggerType !== "code_job"
      );
      dispatch({ type: "LOAD", payload: onlyApps });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [activeProjectId]);

  const fetchFolders = useCallback(async () => {
    if (!activeProjectId) return;
    try {
      const { data } = await api.get("/folders", { params: { projectId: activeProjectId } });
      setFolders(data.folders || data || []);
    } catch (err) {
      toastError(err);
    }
  }, [activeProjectId]);

  useEffect(() => {
    fetchApps();
    fetchFolders();
  }, [fetchApps, fetchFolders]);

  useEffect(() => {
    const socket = connectToSocket();
    socket.emit("joinRoom", "applications");
    socket.emit("joinRoom", "folders");
    socket.on("application", payload => {
      if (payload.action === "delete") dispatch({ type: "DELETE", payload: payload.applicationId });
      else if (payload.application && ["tool", "agent", "collector"].includes(payload.application.triggerType)) {
        // tools/agents → "Agentes de IA"; collectors → "Coletores". Fora desta lista.
      } else dispatch({ type: "UPSERT", payload: payload.application });
    });
    socket.on("folder", () => fetchFolders());
    return () => {
      socket.emit("leaveRoom", "applications");
      socket.emit("leaveRoom", "folders");
      socket.disconnect();
    };
  }, [fetchFolders]);

  // clear checkbox selection when switching folders/project
  useEffect(() => { setSelectedIds([]); }, [selected, activeProjectId]);

  // select a folder AND remember it for this project (persisted across visits)
  const chooseFolder = val => {
    setSelected(val);
    if (activeProjectId) {
      try { localStorage.setItem(folderStoreKey(activeProjectId), String(val)); } catch (e) { /* ignore */ }
    }
  };

  // restore the last selected folder when the active project changes / on load
  useEffect(() => {
    if (!activeProjectId) return;
    let raw = null;
    try { raw = localStorage.getItem(folderStoreKey(activeProjectId)); } catch (e) { /* ignore */ }
    setSelected(raw == null ? "all" : raw === "all" || raw === "none" ? raw : Number(raw));
  }, [activeProjectId]);

  // drop a stale folder selection (folder deleted elsewhere) once folders load
  useEffect(() => {
    if (typeof selected === "number" && folders.length && !folders.some(f => f.id === selected)) {
      chooseFolder("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders]);

  // ---- counts + filtering (client-side, no reload) ----
  const countAll = apps.length;
  const countNone = apps.filter(a => !a.folderId).length;
  const countFor = fid => apps.filter(a => a.folderId === fid).length;

  const scopeApps = apps.filter(a => {
    if (selected === "all") return true;
    if (selected === "none") return !a.folderId;
    return a.folderId === selected;
  });
  const q = searchParam.trim().toLowerCase();
  const visibleApps = q ? scopeApps.filter(a => (a.name || "").toLowerCase().includes(q)) : scopeApps;
  const { sorted: sortedApps } = useSortable(visibleApps, appVal);
  // apps desativadas sempre no final (mantém a ordenação dentro de cada grupo)
  const orderedApps = [
    ...sortedApps.filter(a => a.active !== false),
    ...sortedApps.filter(a => a.active === false),
  ];

  const scopeLabel =
    selected === "all"
      ? "Todas as automações"
      : selected === "none"
      ? "Sem pasta"
      : (folders.find(f => f.id === selected) || {}).name || "Pasta";

  // ---- folder actions ----
  const createFolder = async () => {
    const name = (newFolder || "").trim();
    if (!name) { setNewFolder(null); return; }
    try {
      const { data } = await api.post("/folders", { name, projectId: activeProjectId });
      setNewFolder(null);
      await fetchFolders();
      if (data && data.id) chooseFolder(data.id);
    } catch (err) {
      toastError(err);
    }
  };
  const renameFolder = async f => {
    const name = await prompt({ title: "Renomear pasta", label: "Nome da pasta", defaultValue: f.name, confirmText: "Renomear" });
    if (!name || name.trim() === f.name) return;
    try {
      await api.put(`/folders/${f.id}`, { name: name.trim() });
      fetchFolders();
    } catch (err) {
      toastError(err);
    }
  };
  const deleteFolder = async f => {
    if (!(await confirm({ title: "Excluir pasta", message: `Excluir a pasta "${f.name}"? As automações dela ficam sem pasta (não são apagadas).`, confirmText: "Excluir", danger: true }))) return;
    try {
      await api.delete(`/folders/${f.id}`);
      if (selected === f.id) chooseFolder("all");
      fetchFolders();
      fetchApps();
    } catch (err) {
      toastError(err);
    }
  };

  // ---- checkbox selection ----
  const visibleIds = visibleApps.map(a => a.id);
  const sel = selectedIds.filter(id => visibleIds.includes(id));
  const toggleOne = id => setSelectedIds(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const allSel = visibleIds.length > 0 && sel.length === visibleIds.length;
  const someSel = sel.length > 0 && !allSel;
  const selectBy = kind => {
    setSelMenu(null);
    if (kind === "all") setSelectedIds(visibleIds);
    else if (kind === "active") setSelectedIds(visibleApps.filter(a => a.active !== false).map(a => a.id));
    else if (kind === "inactive") setSelectedIds(visibleApps.filter(a => a.active === false).map(a => a.id));
    else setSelectedIds([]);
  };

  // ---- app actions (single + bulk) ----
  // Recarregar é responsabilidade DESTES helpers, não de cada chamador: são
  // usados pela ação individual e pela ação em lote, e sem isto a lista fica
  // mostrando o que já foi apagado/movido até alguém atualizar a página.
  const moveAppsTo = async (ids, folderId) => {
    try {
      await Promise.all(ids.map(id => api.put(`/applications/${id}/folder`, { folderId })));
    } catch (err) {
      toastError(err);
    } finally {
      fetchApps(); // mesmo com falha parcial: parte pode ter movido
    }
  };
  const deleteApps = async ids => {
    try {
      await Promise.all(ids.map(id => api.delete(`/applications/${id}`)));
    } catch (err) {
      toastError(err);
    } finally {
      fetchApps();
    }
  };
  // move um app para OUTRO projeto (desassocia a pasta — pastas são por projeto).
  // Como a lista é filtrada pelo projeto ativo, o app some daqui após mover.
  const moveAppToProject = async (id, projectId) => {
    try {
      await api.put(`/applications/${id}/project`, { projectId });
      const proj = projects.find(p => p.id === projectId);
      toast.success(`Movido para "${(proj && proj.name) || "projeto"}".`);
      fetchApps();
    } catch (err) {
      toastError(err);
    }
  };
  // duplica a app (cópia como rascunho não publicado, no mesmo projeto/pasta)
  const duplicateApp = async app => {
    setAppMenu(null);
    try {
      await api.post(`/applications/${app.id}/duplicate`);
      toast.success(`"${app.name}" duplicada.`);
      fetchApps();
    } catch (err) {
      toastError(err);
    }
  };
  // ativa/desativa o fluxo. Desativado: não roda (webhook/agendado) e libera a
  // vaga no limite de fluxos ativos do plano.
  const toggleActive = async app => {
    setAppMenu(null);
    const next = app.active === false;
    try {
      await api.put(`/applications/${app.id}/active`, { active: next });
      toast.success(next ? `"${app.name}" ativada.` : `"${app.name}" desativada.`);
      fetchApps();
    } catch (err) {
      toastError(err);
    }
  };
  const closeMenus = () => { setAppMenu(null); setPickMenu(null); setProjMenu(null); };
  const openPick = (anchor, onPick) => setPickMenu({ anchor, onPick });
  const pick = async folderId => {
    if (pickMenu && pickMenu.onPick) await pickMenu.onPick(folderId);
    closeMenus();
  };
  const confirmDeleteOne = async app => {
    closeMenus();
    if (!(await confirm({ title: "Excluir automação", message: `Excluir "${app.name}"? Esta ação não pode ser desfeita.`, confirmText: "Excluir", danger: true }))) return;
    await deleteApps([app.id]);
  };
  const confirmDeleteBulk = async () => {
    if (!sel.length) return;
    if (!(await confirm({ title: "Excluir automações", message: `Excluir ${sel.length} automação(ões)? Esta ação não pode ser desfeita.`, confirmText: "Excluir", danger: true }))) return;
    await deleteApps(sel);
    setSelectedIds([]);
  };

  const newAppUrl = () => {
    let url = `/applications/new?projectId=${activeProjectId}`;
    if (typeof selected === "number") url += `&folderId=${selected}`;
    return url;
  };

  // ---- status: dot + label + versão mono (sistema único) ----
  const renderStatus = app => {
    let color = T.ink3;
    let label = i18n.t("apps.draftStatus");
    let ver = null;
    // Etapa 4 (MCP): "em desenvolvimento" é rótulo de obra em andamento — tem
    // precedência na exibição (fica fora do limite do plano).
    if (app.inDevelopment) {
      return (
        <span className={classes.status}>
          <span className={classes.dot} style={{ background: "#B26A00" }} />
          Em desenvolvimento
          {app.publishedVersion && <span className={classes.ver}>v{app.publishedVersion}</span>}
        </span>
      );
    }
    // desativado não é "publicado": mostra "Desativado" (mantém a versão p/ saber
    // que há o que reativar)
    if (app.active === false) {
      return (
        <span className={classes.status}>
          <span className={classes.dot} style={{ background: "#9AA3AD" }} />
          Desativado
          {app.publishedVersion && <span className={classes.ver}>v{app.publishedVersion}</span>}
        </span>
      );
    }
    if (app.publishedVersion) {
      if (app.dirty) {
        color = T.warn;
        label = i18n.t("apps.unpublishedShort");
        ver = `v${app.publishedVersion}`;
      } else {
        color = T.ok;
        label = i18n.t("apps.publishedShort");
        ver = `v${app.publishedVersion}`;
      }
    }
    return (
      <span className={classes.status}>
        <span className={classes.dot} style={{ background: color }} />
        {label}
        {ver && <span className={classes.ver}>{ver}</span>}
      </span>
    );
  };

  const SideItem = ({ active, icon, label, count, onClick, menu }) => (
    <div className={clsx(classes.item, active && classes.itemActive)} onClick={onClick}>
      <span className={classes.itemIcon}>{icon}</span>
      <span className={classes.itemLabel}>{label}</span>
      <span className={classes.itemCount}>{count}</span>
      {menu}
    </div>
  );

  return (
    <div className={classes.page}>
      {/* ---- folders rail (scoped to the active project) ---- */}
      <div className={classes.side}>
        <div className={classes.sideHead}>
          <Typography className={classes.sideTitle}>Pastas</Typography>
          <span className={classes.sideCount}>{folders.length}</span>
          <Tooltip title="Nova pasta">
            <span>
              <IconButton size="small" className={classes.folderAddBtn} onClick={() => setNewFolder("")} disabled={!activeProjectId}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
        <div className={classes.list}>
          {newFolder !== null && (
            <div className={classes.newProjRow} style={{ padding: "2px 4px 8px" }}>
              <TextField
                autoFocus
                size="small"
                variant="outlined"
                placeholder="Nome da pasta"
                value={newFolder}
                onChange={e => setNewFolder(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") createFolder();
                  if (e.key === "Escape") setNewFolder(null);
                }}
                onBlur={createFolder}
                fullWidth
              />
            </div>
          )}
          <SideItem active={selected === "all"} icon={<AllIcon fontSize="inherit" />} label="Minhas automações" count={countAll} onClick={() => chooseFolder("all")} />
          <div className={classes.sep} />
          {folders.map(f => (
            <SideItem
              key={f.id}
              active={selected === f.id}
              icon={<FolderIcon fontSize="inherit" />}
              label={f.name}
              count={countFor(f.id)}
              onClick={() => chooseFolder(f.id)}
              menu={<FolderMenu className={classes.itemMenu} onRename={() => renameFolder(f)} onDelete={() => deleteFolder(f)} />}
            />
          ))}
          <div className={classes.sep} />
          <SideItem active={selected === "none"} icon={<InboxIcon fontSize="inherit" />} label="Sem pasta" count={countNone} onClick={() => chooseFolder("none")} />
        </div>
      </div>

      {/* ---- apps of the selected folder ---- */}
      <div className={classes.main}>
        <div className={classes.wrap}>
          <div className={classes.header}>
            <div className={classes.headIcon}><FlowIcon /></div>
            <Typography className={classes.title}>{scopeLabel}</Typography>
            <span className={classes.titleCount}>{visibleApps.length}</span>
            <div className={classes.grow} />
            <TextField
              className={classes.search}
              placeholder={i18n.t("apps.search")}
              variant="outlined"
              size="small"
              value={searchParam}
              onChange={e => setSearchParam(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            />
            <Button variant="contained" disableElevation className={classes.primaryBtn} startIcon={<AddIcon />} disabled={!activeProjectId} onClick={() => history.push(newAppUrl())}>
              {i18n.t("apps.new")}
            </Button>
          </div>

          <div className={classes.bulkBar}>
              <span className={classes.selCtl}>
                <Checkbox size="small" color="default" checked={allSel} indeterminate={someSel} onChange={() => setSelectedIds(allSel ? [] : visibleIds)} />
                <IconButton size="small" className={classes.selCaret} onClick={e => setSelMenu({ anchor: e.currentTarget })}><ArrowDropDownIcon fontSize="small" /></IconButton>
              </span>
              {sel.length > 0 && (
                <>
                  <span className={classes.bulkCount}>{sel.length} selecionada(s)</span>
                  <Button size="small" style={{ textTransform: "none" }} startIcon={<FolderIcon fontSize="small" />} onClick={e => openPick(e.currentTarget, fid => moveAppsTo(sel, fid).then(() => setSelectedIds([])))}>
                    Mover para pasta…
                  </Button>
                  {isProtected ? (
                    <Tooltip title={PROTECTED_MSG}>
                      <span>
                        <Button size="small" disabled style={{ textTransform: "none" }}>
                          Excluir
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button size="small" style={{ color: T.danger, textTransform: "none" }} onClick={confirmDeleteBulk}>
                      Excluir
                    </Button>
                  )}
                  <div className={classes.grow} />
                  <Button size="small" style={{ textTransform: "none" }} onClick={() => setSelectedIds([])}>Limpar</Button>
                </>
              )}
            </div>

          {loading ? (
            <div className={classes.appList}>
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className={clsx(classes.appCard, classes.skCard)}>
                  <div className={clsx(classes.appAvatar, classes.skBox, classes.skAvatar)} />
                  <div className={classes.appMain}>
                    <div className={clsx(classes.skBox, classes.skLabel)} />
                    <div className={clsx(classes.skBox, classes.skLine)} style={{ width: "45%" }} />
                  </div>
                  <div className={classes.colStatus}>
                    <div className={clsx(classes.skBox, classes.skLabel)} />
                    <div className={clsx(classes.skBox, classes.skLine)} style={{ width: 80 }} />
                  </div>
                  <div className={classes.colDate}>
                    <div className={clsx(classes.skBox, classes.skLabel)} />
                    <div className={clsx(classes.skBox, classes.skLine)} style={{ width: 110 }} />
                  </div>
                  <div className={classes.colDate}>
                    <div className={clsx(classes.skBox, classes.skLabel)} />
                    <div className={clsx(classes.skBox, classes.skLine)} style={{ width: 110 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : visibleApps.length === 0 ? (
            <div className={classes.empty}>{i18n.t("apps.empty")}</div>
          ) : (
            <div className={classes.appList}>
              {orderedApps.map(app => (
                <div
                  key={app.id}
                  className={clsx(classes.appCard, selectedIds.includes(app.id) && classes.appCardSel, app.active === false && classes.appCardInactive)}
                  onClick={() => history.push(`/applications/${app.code || app.id}`)}
                >
                  <span onClick={e => e.stopPropagation()} style={{ display: "flex" }}>
                    <Checkbox size="small" color="default" checked={selectedIds.includes(app.id)} onChange={() => toggleOne(app.id)} />
                  </span>
                  <div className={classes.appAvatar}><AllIcon /></div>

                  <div className={classes.appMain}>
                    <div className={classes.infoLabel}>{i18n.t("apps.name")}</div>
                    <div className={classes.nameRow}>
                      <span className={classes.appName}>{app.name}</span>
                      {app.active === false && (
                        <span className={classes.deactivatedPill}><PauseIcon />Desativado</span>
                      )}
                    </div>
                    {/* resumo do readme do fluxo: lê-se sem abrir a aplicação.
                        Só a 1ª linha útil, sem marcação — o markdown completo
                        vive no modal de notas. */}
                    {resumoNota(app.notes) && (
                      <div className={classes.appNote} title={resumoNota(app.notes)}>{resumoNota(app.notes)}</div>
                    )}
                  </div>

                  <div className={classes.colStatus}>
                    <div className={classes.infoLabel}>{i18n.t("apps.status")}</div>
                    {renderStatus(app)}
                  </div>

                  <div className={classes.colDate}>
                    <div className={classes.infoLabel}>{i18n.t("apps.lastRunAt")}</div>
                    <span className={classes.colDateVal}>{app.lastRunAt ? format(new Date(app.lastRunAt), "dd/MM/yyyy HH:mm") : "—"}</span>
                  </div>

                  <div className={classes.colDate}>
                    <div className={classes.infoLabel}>{i18n.t("apps.updatedAt")}</div>
                    <span className={classes.colDateVal}>{app.updatedAt ? format(new Date(app.updatedAt), "dd/MM/yyyy HH:mm") : "—"}</span>
                  </div>

                  <span onClick={e => e.stopPropagation()} style={{ display: "flex", marginLeft: "auto" }}>
                    <IconButton size="small" className={classes.kebab} onClick={e => setAppMenu({ anchor: e.currentTarget, app })}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* per-app (...) menu */}
      <Menu anchorEl={appMenu ? appMenu.anchor : null} open={!!appMenu} onClose={closeMenus} PaperProps={{ className: menu.paper }} MenuListProps={{ disablePadding: true }}>
        <MenuItem className={menu.item} onClick={e => openPick(e.currentTarget, fid => moveAppsTo([appMenu.app.id], fid))}>
          <FolderIcon fontSize="small" />
          Mover para pasta…
        </MenuItem>
        <MenuItem className={menu.item} onClick={e => setProjMenu({ anchor: e.currentTarget, app: appMenu.app })}>
          <ProjectIcon fontSize="small" />
          Mover para projeto…
        </MenuItem>
        <MenuItem className={menu.item} onClick={() => appMenu && duplicateApp(appMenu.app)}>
          <CopyIcon fontSize="small" />
          Duplicar automação
        </MenuItem>
        <MenuItem className={menu.item} onClick={() => appMenu && toggleActive(appMenu.app)}>
          {appMenu && appMenu.app.active === false ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
          {appMenu && appMenu.app.active === false ? "Ativar fluxo" : "Desativar fluxo"}
        </MenuItem>
        <div className={menu.divider} />
        {isProtected ? (
          <Tooltip title={PROTECTED_MSG} placement="left">
            <span>
              <MenuItem className={clsx(menu.item, menu.danger)} disabled>
                <DeleteIcon fontSize="small" />
                Excluir automação
              </MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem className={clsx(menu.item, menu.danger)} onClick={() => appMenu && confirmDeleteOne(appMenu.app)}>
            <DeleteIcon fontSize="small" />
            Excluir automação
          </MenuItem>
        )}
      </Menu>

      {/* menu do cabeçalho de seleção (todos/nenhum/ativos/inativos) */}
      <Menu anchorEl={selMenu ? selMenu.anchor : null} open={!!selMenu} onClose={() => setSelMenu(null)} PaperProps={{ className: menu.paper }} MenuListProps={{ disablePadding: true }}>
        <MenuItem className={menu.item} onClick={() => selectBy("all")}>Todas</MenuItem>
        <MenuItem className={menu.item} onClick={() => selectBy("none")}>Nenhuma</MenuItem>
        <MenuItem className={menu.item} onClick={() => selectBy("active")}>Somente ativas</MenuItem>
        <MenuItem className={menu.item} onClick={() => selectBy("inactive")}>Somente inativas</MenuItem>
      </Menu>

      {/* folder picker — submenu of the (...) menu and target of the bulk button */}
      <Menu
        anchorEl={pickMenu ? pickMenu.anchor : null}
        open={!!pickMenu}
        onClose={closeMenus}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: menu.paper }}
        MenuListProps={{ disablePadding: true }}
      >
        <MenuItem className={menu.item} onClick={() => pick(null)}>Sem pasta</MenuItem>
        {folders.map(f => (
          <MenuItem key={f.id} className={menu.item} onClick={() => pick(f.id)}>{f.name}</MenuItem>
        ))}
      </Menu>

      {/* project picker — submenu do menu (...) para "Mover para projeto…" */}
      <Menu
        anchorEl={projMenu ? projMenu.anchor : null}
        open={!!projMenu}
        onClose={closeMenus}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: menu.paper }}
        MenuListProps={{ disablePadding: true }}
      >
        {projects.length <= 1 && (
          <MenuItem className={menu.item} disabled>Nenhum outro projeto</MenuItem>
        )}
        {projects.map(p => {
          const isCurrent = p.id === activeProjectId;
          return (
            <MenuItem
              key={p.id}
              className={menu.item}
              disabled={isCurrent}
              onClick={() => {
                const app = projMenu && projMenu.app;
                closeMenus();
                if (app) moveAppToProject(app.id, p.id);
              }}
            >
              {p.name}{isCurrent ? " (atual)" : ""}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

// kebab menu on a folder row (rename / delete), stops row-click propagation
const FolderMenu = ({ className, onRename, onDelete }) => {
  const [anchor, setAnchor] = useState(null);
  const open = e => { e.stopPropagation(); setAnchor(e.currentTarget); };
  const close = e => { if (e) e.stopPropagation(); setAnchor(null); };
  return (
    <>
      <IconButton size="small" className={className} onClick={open}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
        <MenuItem onClick={e => { close(e); onRename(); }}>Renomear</MenuItem>
        <MenuItem onClick={e => { close(e); onDelete(); }} style={{ color: "#C0453B" }}>Excluir pasta</MenuItem>
      </Menu>
    </>
  );
};

export default Applications;
